import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { rbacService, RBACPermission } from '@/services/rbac';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { NavigationModule, PermissionType } from '@/types/enums';

export const RoleCreatePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    roleName: '',
    displayName: '',
    description: ''
  });
  const [permissions, setPermissions] = useState<RBACPermission[]>([]);

  const handleSave = async () => {
    if (!formData.roleName || !formData.displayName) {
      alert('Role name and display name are required');
      return;
    }

    try {
      setSaving(true);
      await rbacService.createRole({
        roleName: formData.roleName,
        displayName: formData.displayName,
        description: formData.description,
        permissions
      });
      navigate('/rbac/roles');
    } catch (error) {
      console.error('Error creating role:', error);
      alert('Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (module: NavigationModule, permission: PermissionType) => {
    setPermissions(prev => {
      const modulePermission = prev.find(p => p.module === module);
      
      if (modulePermission) {
        const hasPermission = modulePermission.permissions.includes(permission);
        const updatedPermissions = hasPermission
          ? modulePermission.permissions.filter(p => p !== permission)
          : [...modulePermission.permissions, permission];

        return prev.map(p => 
          p.module === module 
            ? { ...p, permissions: updatedPermissions }
            : p
        );
      } else {
        // Add new module permission
        return [...prev, { module, permissions: [permission] }];
      }
    });
  };

  const toggleAllPermissionsForModule = (module: NavigationModule) => {
    setPermissions(prev => {
      const modulePermission = prev.find(p => p.module === module);
      const allPermissions = Object.values(PermissionType);
      
      if (modulePermission && modulePermission.permissions.length === allPermissions.length) {
        // If all permissions are selected, unselect all
        return prev.filter(p => p.module !== module);
      } else {
        // Select all permissions for this module
        const updatedPermissions = prev.filter(p => p.module !== module);
        return [...updatedPermissions, { module, permissions: allPermissions }];
      }
    });
  };

  const hasPermission = (module: NavigationModule, permission: PermissionType) => {
    const modulePermission = permissions.find(p => p.module === module);
    return modulePermission?.permissions.includes(permission) || false;
  };

  const hasAllPermissionsForModule = (module: NavigationModule) => {
    const modulePermission = permissions.find(p => p.module === module);
    const allPermissions = Object.values(PermissionType);
    return modulePermission && modulePermission.permissions.length === allPermissions.length;
  };

  const getModuleLabel = (module: NavigationModule) => {
    return module.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getModuleDescription = (module: NavigationModule) => {
    switch (module) {
      case NavigationModule.DASHBOARD:
        return 'Overview and analytics dashboard for monitoring system performance and key metrics.';
      case NavigationModule.USER_MANAGEMENT:
        return 'Manage user accounts, profiles, and access permissions across the platform.';
      case NavigationModule.SYSTEM_ADMINISTRATION:
        return 'Configure tenant-specific settings, policies, and administrative functions.';
      case NavigationModule.TRANSFORMATION_MANAGEMENT:
        return 'Plan, execute, and monitor business transformation initiatives and projects.';
      case NavigationModule.KPI_MANAGEMENT:
        return 'Define, track, and analyze key performance indicators and business metrics.';
      case NavigationModule.PROCESS_MODELING:
        return 'Design, document, and optimize business processes and workflows.';
      case NavigationModule.CHANGE_MANAGEMENT:
        return 'Manage organizational change initiatives, communications, and stakeholder engagement.';
      case NavigationModule.DATA_IMPORT:
        return 'Import and validate data from external sources and legacy systems.';
      case NavigationModule.REPORTING_ANALYTICS:
        return 'Generate reports, analytics, and insights for decision-making.';
      case NavigationModule.BUSINESS_MANAGEMENT:
        return 'Manage business entities, hierarchies, and organizational structures.';
      case NavigationModule.ACCESS_MANAGEMENT:
        return 'Control and monitor access to system resources and sensitive data.';
      case NavigationModule.DATA_CONNECTION:
        return 'Manage data integrations, APIs, and external system connections.';
      case NavigationModule.PARTNER_MANAGEMENT:
        return 'Manage partner relationships, collaborations, and external stakeholders.';
      case NavigationModule.TEMPLATES_BEST_PRACTICES:
        return 'Access templates, frameworks, and best practices for consistent implementation.';
      default:
        return 'Module for managing platform functionality and data.';
    }
  };

  const getPermissionLabel = (permission: PermissionType) => {
    switch (permission) {
      case PermissionType.READ:
        return 'Read';
      case PermissionType.WRITE:
        return 'Write';
      case PermissionType.DELETE:
        return 'Delete';
      case PermissionType.ADMIN:
        return 'Admin';
      default:
        return permission;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/rbac/roles')}
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>{t('common.back')}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
              Create New Role
            </h1>
            <p className="text-[var(--color-secondary-text)]">
              Define a new custom role with specific permissions
            </p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate('/rbac/roles')}
            className="flex items-center space-x-2"
          >
            <XMarkIcon className="w-4 h-4" />
            <span>Cancel</span>
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.roleName || !formData.displayName}
            className="flex items-center space-x-2"
          >
            <CheckIcon className="w-4 h-4" />
            <span>{saving ? 'Creating...' : 'Create Role'}</span>
          </Button>
        </div>
      </div>

      {/* Role Info */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
          Basic Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Role Name *
            </label>
            <input
              type="text"
              value={formData.roleName}
              onChange={(e) => setFormData(prev => ({ ...prev, roleName: e.target.value }))}
              placeholder="e.g., TRANSFORMATION_MANAGER"
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)]"
            />
            <p className="text-xs text-[var(--color-secondary-text)] mt-1">
              Use uppercase with underscores (e.g., ROLE_NAME)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Display Name *
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
              placeholder="e.g., Transformation Manager"
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('roles.roleDescriptionPlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)]"
            />
          </div>
        </div>
      </Card>

      {/* Permissions */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
          Module Permissions
        </h2>
        <p className="text-sm text-[var(--color-secondary-text)] mb-4">
          Select the modules and permissions this role should have access to.
        </p>

        <div className="space-y-6">
          {Object.values(NavigationModule).map((module) => (
            <div key={module} className="border border-[var(--color-border)] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium text-[var(--color-secondary-text)]">
                  {getModuleLabel(module)}
                </h3>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasAllPermissionsForModule(module)}
                    onChange={() => toggleAllPermissionsForModule(module)}
                    className="rounded border-[var(--color-border)] text-[var(--color-primary-bg)] focus:ring-[var(--color-primary-bg)]"
                  />
                  <span className="text-sm font-medium text-[var(--color-secondary-text)]">
                    Select All
                  </span>
                </label>
              </div>
              
              <p className="text-sm text-[var(--color-secondary-text)] mb-3 italic">
                {getModuleDescription(module)}
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.values(PermissionType).map((permission) => (
                  <label key={permission} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasPermission(module, permission)}
                      onChange={() => togglePermission(module, permission)}
                      className="rounded border-[var(--color-border)] text-[var(--color-primary-bg)] focus:ring-[var(--color-primary-bg)]"
                    />
                    <span className="text-sm text-[var(--color-secondary-text)]">
                      {getPermissionLabel(permission)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
